import { useState } from "react"
import { AircraftImage } from "@nova/component-aircraft-images"
import { AlertContainer } from "@nova/component-alert-container"
import { ButtonSecondary } from "@nova/component-btn"
import { getDisplayableAircraftData } from "@nova/constants"
import { AircraftManualStatus } from "@nova/schemas"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { AircraftStatusBadge } from "./AircraftStatusBadge"
import { useAircraftMaintenanceStatus } from "./AircraftStatusQueries"
import { EditAircraftModal } from "./EditAircraftModal"
import { QueryableResults } from "@nova/queryable"
import { aircraftQuery } from "./AircraftQueries"
import * as Icons from "@nova/component-icons"
import { TabHeader } from "themes/default-tailwind/buttons/TabHeader"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectTab, setTab } from "./slice"
import { ExpandableContent } from "@nova/component-expandable-content"

type AircraftQueryResult = QueryableResults<typeof aircraftQuery>

const AircraftPageTabSwitcher = ({ aircraft }: { aircraft: AircraftQueryResult[number] }) => {
    const dispatch = useAppDispatch()
    const tab = useAppSelector(selectTab)
    const { overdueCount } = useAircraftMaintenanceStatus(
        aircraft.id,
        aircraft.manual_status as AircraftManualStatus
    )
    return (
        <div className="flex">
            <TabHeader
                currentMode={tab}
                mode="overview"
                setActiveMode={() => dispatch(setTab("overview"))}
                title="Overview"
            />
            <TabHeader
                currentMode={tab}
                mode="history"
                setActiveMode={() => dispatch(setTab("history"))}
                title="History"
            />
            <TabHeader
                currentMode={tab}
                mode="maintenance"
                setActiveMode={() => dispatch(setTab("maintenance"))}
                title="Maintenance"
                icon={overdueCount > 0 ? Icons.Warning : undefined}
                iconClassName="!text-red-500"
            />
            <div className="flex-1 border-b-2 border-b-white-200" />
        </div>
    )
}

export const AircraftPageHeader = ({ aircraft }: { aircraft: AircraftQueryResult[number] }) => {
    const { displayName, make, model } = getDisplayableAircraftData(aircraft)
    const subtitle = [make, model].filter(Boolean).join(" ")
    const [missingModelDismissed, setMissingModelDismissed] = useState(false)
    const showMissingModelBanner = !aircraft.model && !missingModelDismissed

    return (
        <>
            <div className="relative w-full">
                <div className="absolute -right-0 -bottom-52 -top-40 w-[77%] overflow-hidden">
                    <AircraftImage
                        make={aircraft.manufacturer}
                        model={aircraft.model}
                        quality="full"
                        className="w-full h-full object-cover object-center opacity-40"
                    />
                    <div
                        className="absolute inset-0"
                        style={{
                            background:
                                "radial-gradient(ellipse 60% 80% at 50% 50%, transparent 40%, #121216 80%)",
                        }}
                    />
                </div>
                <div className="relative z-10 h-full flex flex-col gap-2 justify-between pt-4">
                    <div className="flex items-center gap-6">
                        {showMissingModelBanner ? (
                            <div className="flex-1 min-w-0">
                                <AlertContainer
                                    type="warning"
                                    variant="banner"
                                    title="This drone is missing a model name."
                                    onClose={() => setMissingModelDismissed(true)}
                                />
                            </div>
                        ) : (
                            <div className="flex-1" />
                        )}
                        <AircraftNextMaintenance aircraft={aircraft} />
                        <EditAircraftDetailsButton aircraft={aircraft} />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <AircraftStatusBadge
                            aircraftId={aircraft.id}
                            manualStatus={aircraft.manual_status as AircraftManualStatus}
                        />
                        <div className="flex flex-col gap-1">
                            <h1 className="text-[48px] leading-tight text-white telegraf">
                                {displayName}
                            </h1>
                            <h3 className="text-gray-300">{subtitle}</h3>
                        </div>
                    </div>
                </div>
            </div>
            <div className="py-4 isolate">
                <AircraftPageTabSwitcher aircraft={aircraft} />
            </div>
        </>
    )
}

export const AircraftPageStickyHeader = ({
    aircraft,
    visible,
}: {
    aircraft: AircraftQueryResult[number]
    visible: boolean
}) => {
    const { displayName, make, model } = getDisplayableAircraftData(aircraft)
    const subtitle = [make, model].filter(Boolean).join(" ")
    return (
        <ExpandableContent isOpen={visible}>
            <div className="relative bg-black-500 py-6">
                <div className="absolute inset-0 backdrop-blur-md" />
                <div className="relative z-10">
                    <div className="flex flex-col gap-1.5">
                        <AircraftStatusBadge
                            aircraftId={aircraft.id}
                            manualStatus={aircraft.manual_status as AircraftManualStatus}
                        />
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex flex-col gap-1">
                                <div className="text-[21px] telegraf text-white">{displayName}</div>
                                <div className="text-gray-300">{subtitle}</div>
                            </div>
                            <div className="flex gap-x-6 gap-y-1.5 items-center flex-wrap-reverse pr-1">
                                <AircraftNextMaintenance aircraft={aircraft} />
                                <EditAircraftDetailsButton aircraft={aircraft} />
                            </div>
                        </div>
                    </div>
                    <div className="pt-3">
                        <AircraftPageTabSwitcher aircraft={aircraft} />
                    </div>
                </div>
            </div>
        </ExpandableContent>
    )
}

function EditAircraftDetailsButton({ aircraft }: { aircraft: AircraftQueryResult[number] }) {
    const modal = useConfirmationModalContext()
    const { isEditor } = useWorkspacePermissions()

    const handleEditClick = async () => {
        await modal.confirm({
            RenderComponent: ({ cancel, confirm }) => (
                <EditAircraftModal
                    aircraftId={aircraft.id}
                    initialValues={{
                        nickname: aircraft.nickname,
                        manufacturer: aircraft.manufacturer,
                        model: aircraft.model,
                        serial_number: aircraft.serial_number,
                        registration_number: aircraft.registration_number,
                        purchased_at: aircraft.purchased_at,
                        warranty_expires_at: aircraft.warranty_expires_at,
                        notes: aircraft.notes,
                        manual_status: aircraft.manual_status as AircraftManualStatus,
                    }}
                    onCancel={cancel}
                    onSubmit={confirm}
                />
            ),
        })
    }

    if (!isEditor) return null
    return (
        <ButtonSecondary
            title="Edit Details"
            LeftIcon={Icons.PencilSquare}
            onClick={handleEditClick}
            className="pointer-events-auto"
        />
    )
}

function AircraftNextMaintenance({ aircraft }: { aircraft: AircraftQueryResult[number] }) {
    const warrantyStr = aircraft.warranty_expires_at
        ? new Date(aircraft.warranty_expires_at).toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
          })
        : null
    if (!warrantyStr) return null
    return (
        <span className="text-sm text-gray-300">
            Next maintenance due: <span className="text-white font-medium">{warrantyStr}</span>
        </span>
    )
}
